import { Link } from "react-router-dom";
import { ArrowRight, Move, Smartphone, Users } from "lucide-react";
import { BrandLogo, SiteFooter } from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/785a6fc6-cc23-4d53-a250-95194c79bd54/images/b1fa2caff84cad21dd24699082c835596c9f3e20091ffc444c7b515a159fbcd8.jpeg";

const FEATURES = [
  { icon: Move, title: "Drag your RSVP button", body: "Put it exactly where it looks best on your image. It stays put on every screen." },
  { icon: Smartphone, title: "Made for phones", body: "Guests tap, answer in seconds and add it straight to their calendar." },
  { icon: Users, title: "Know your headcount", body: "Yes, no, maybe and plus-ones tallied live in your dashboard." },
];

const primaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 font-semibold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-blue-800 hover:text-white active:scale-95";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="confetti-bg-white font-manrope min-h-screen text-slate-900">
      <header data-testid="site-header" className="fixed left-0 top-0 z-50 w-full border-b border-slate-200/60 bg-white/70 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <BrandLogo />
          <Link to={user ? "/dashboard" : "/login"} data-testid="landing-login-btn" className={`${primaryBtn} px-5 py-2.5 text-sm`}>
            {user ? "Dashboard" : "Host login"}
          </Link>
        </div>
      </header>
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:pt-40">
        <div className="rise">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-xs font-medium text-amber-700 sm:text-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
            Invitations, RSVPs, done.
          </span>
          <h1 data-testid="landing-title" className="font-outfit mt-6 text-4xl font-bold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Events &amp; <span className="text-blue-700">Invitations</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Upload your invite art, drop in the details and share one link. Guests RSVP from their phone in seconds.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to={user ? "/events/new" : "/signup"} data-testid="landing-get-started-btn" className={`${primaryBtn} px-6 py-3.5 text-base`}>
              Create your invite <ArrowRight className="h-5 w-5" />
            </Link>
            <span className="text-sm text-slate-500 sm:ml-2">Free for hosts. No app for guests.</span>
          </div>
          <div className="mt-16 grid gap-5 sm:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="rise rounded-2xl border border-slate-200 bg-white p-6" style={{ animationDelay: `${150 + i * 120}ms` }}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-outfit mt-5 text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rise relative mx-auto w-full max-w-sm" style={{ animationDelay: "200ms" }}>
          <div className="relative rotate-2 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.5)]">
            <div className="relative overflow-hidden rounded-[1.5rem]">
              <img src={HERO} alt="Party table with confetti and balloons" className="block h-auto w-full" />
              <div className="absolute left-[28%] top-[56%] w-[40%] -translate-x-1/2 -translate-y-1/2 -rotate-6 text-center">
                <p className="font-display text-lg font-extrabold leading-none text-slate-900">Mia turns 30</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">Sat · 7 PM</p>
              </div>
              <span className="rsvp-pulse absolute left-1/2 top-[86%] -translate-x-1/2 -translate-y-1/2 rounded-xl border-2 border-white/80 bg-blue-700 px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white">
                RSVP
              </span>
            </div>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
